import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "The Orion Nebula",
    subtitle: "M42 - A Stellar Nursery in Our Galaxy",
    intro: "M42, popularly known as the Orion Nebula, is actually a diffuse nebula within our own Milky Way Galaxy, not a separate galaxy itself. It is located approximately 1,344 light-years from Earth in the Orion Arm of the Milky Way.",
    skyLocation: "Sky Location",
    skyLocationIntro: "You can find M42 in the constellation Orion. It is famously situated in the center of Orion's Sword, which appears to hang vertically just south of the three prominent stars of Orion's Belt.",
    coordinates: "Coordinates (J2000)",
    coordinatesDesc: "Right Ascension (RA) 5h 35.4m, Declination (Dec) -05° 27′",
    visibility: "Visibility",
    visibilityDesc: "It is one of the brightest nebulae in the night sky (magnitude 4.0) and is visible to the naked eye as a \"fuzzy\" star-like patch.",
    bestTime: "Best Time to View",
    bestTimeDesc: "It is a prime winter object in the Northern Hemisphere, reaching its highest point in the sky around midnight in mid-December or 10:00 PM in mid-January.",
    observationTip: "Observation Tip",
    observationTipDesc: "Are you planning to view M42 with binoculars or a telescope, and would you like tips for identifying the Trapezium Cluster at its core?"
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Oriontåken",
    subtitle: "M42 - En Stellær Fødestue i Vår Galakse",
    intro: "M42, populært kjent som Oriontåken, er faktisk en diffus tåke i vår egen Melkevei-galakse, ikke en egen galakse. Den ligger omtrent 1 344 lysår fra Jorden i Orion-armen av Melkeveien.",
    skyLocation: "Posisjon på Himmelen",
    skyLocationIntro: "Du kan finne M42 i stjernebildet Orion. Den ligger berømt i sentrum av Orions Sverd, som ser ut til å henge vertikalt like sør for de tre fremtredende stjernene i Orions Belte.",
    coordinates: "Koordinater (J2000)",
    coordinatesDesc: "Rektascensjon (RA) 5t 35.4m, Deklinasjon (Dec) -05° 27′",
    visibility: "Synlighet",
    visibilityDesc: "Det er en av de lyseste tåkene på nattehimmelen (størrelsesklasse 4,0) og er synlig for det blotte øye som en \"uklar\" stjerneaktig flekk.",
    bestTime: "Beste Tid for Observasjon",
    bestTimeDesc: "Det er et førsteklasses vinterobjekt på den nordlige halvkule, og når sitt høyeste punkt på himmelen rundt midnatt i midten av desember eller 22:00 i midten av januar.",
    observationTip: "Observasjonstips",
    observationTipDesc: "Planlegger du å se M42 med kikkert eller teleskop, og ønsker du tips for å identifisere Trapezium-klyngen i kjernen?"
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Mgławica Oriona",
    subtitle: "M42 - Gwiezdny Żłobek w Naszej Galaktyce",
    intro: "M42, popularnie znana jako Mgławica Oriona, jest w rzeczywistości mgławicą dyfuzyjną w naszej własnej galaktyce Drogi Mlecznej, a nie osobną galaktyką. Znajduje się około 1 344 lat świetlnych od Ziemi w Ramieniu Oriona Drogi Mlecznej.",
    skyLocation: "Położenie na Niebie",
    skyLocationIntro: "M42 można znaleźć w gwiazdozbiorze Oriona. Jest słynnie usytuowana w centrum Miecza Oriona, który wydaje się wisieć pionowo na południe od trzech wyróżniających się gwiazd Pasa Oriona.",
    coordinates: "Współrzędne (J2000)",
    coordinatesDesc: "Rektascensja (RA) 5h 35.4m, Deklinacja (Dec) -05° 27′",
    visibility: "Widoczność",
    visibilityDesc: "Jest to jedna z najjaśniejszych mgławic na nocnym niebie (wielkość 4,0) i jest widoczna gołym okiem jako \"rozmyta\" gwiazdopodobna plama.",
    bestTime: "Najlepszy Czas na Obserwację",
    bestTimeDesc: "Jest to doskonały zimowy obiekt na półkuli północnej, osiągający najwyższy punkt na niebie około północy w połowie grudnia lub 22:00 w połowie stycznia.",
    observationTip: "Wskazówka Obserwacyjna",
    observationTipDesc: "Czy planujesz obserwować M42 przez lornetkę lub teleskop i chciałbyś uzyskać wskazówki dotyczące identyfikacji Gromady Trapezu w jej jądrze?"
  }
};

export default function OrionDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-pink-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
                data-testid="lang-pl-btn"
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="orion-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="orion-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 via-red-400 to-orange-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Overview */}
            <div>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.intro}
              </p>
            </div>

            {/* Sky Location */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.skyLocation}
              </h2>

              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.skyLocationIntro}
              </p>

              <div className="space-y-6 text-white/80 leading-relaxed">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.coordinates}
                  </h3>
                  <p>{t.coordinatesDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.visibility}
                  </h3>
                  <p>{t.visibilityDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.bestTime}
                  </h3>
                  <p>{t.bestTimeDesc}</p>
                </div>
              </div>
            </div>

            {/* Observation Note */}
            <div className="pt-6 border-t border-white/10">
              <div className="bg-pink-500/10 border border-pink-500/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-pink-300 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.observationTip}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {t.observationTipDesc}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
